import { ITotalItem } from "../SuperTable/subpage/tableTotal";
import { STATUS_CREATED, STATUS_CREATED_CODE } from "../constants/constant";
import { NamePath } from "../constants/interface";
import { accAdd, getThumbUrl } from "../utils/dataUtils";
import { GROUP_NAME } from "./interface";

/**
 * 获取默认值
 * @param initialValue 默认值
 * @param record 当前行数据
 * @param name 列字段名
 * @returns 
 */
export function getInitialValue(initialValue: any, record: any, name?: NamePath) {
  const labelKey = Array.isArray(name) ? name[0] : name;
  if (labelKey && record?.[labelKey] !== undefined) return record[labelKey];
  if (initialValue !== undefined) return initialValue;
  return undefined;
}
/**
 * 获取当前行状态
 * @param recordCode 数据状态
 * @param formCode form状态
 * @returns 
 */
export function getRecordCode(recordCode: number, formCode: number) {
  if (recordCode !== STATUS_CREATED.DEFAULT) {
    return recordCode;
  }
  return formCode || recordCode;
}
/**
 * 修改行状态
 * @param recordStatus 原状态 
 * @returns 
 */
export function updateRecordStatus(recordStatus: number): number {
  if (recordStatus === STATUS_CREATED.DEFAULT) {
    return STATUS_CREATED.UPDATE;
  }
  return recordStatus;
}
/**
 * 可编辑表数据归类
 * @param dataSource 可编辑表数据
 * @returns { effectiveData: 有效的数据; deleteData: 已删除的数据 }
 */
export function formatDataSource(
  dataSource?: any[],
): {
  /** 有效的行数据 */
  effectiveData: any[];
  /** 被删除的行数据 */
  deleteData: any[]
} {
  const effectiveData: any[] = [];
  const deleteData: any[] = [];
  dataSource?.forEach?.(item => {
    const copyItem = { ...item };
    delete copyItem.HISTORY_LIST_DATA;
    if (copyItem[STATUS_CREATED_CODE] === STATUS_CREATED.DELETE) {
      delete copyItem.key;
      deleteData.push(copyItem);
    } else {
      effectiveData.push(copyItem);
    }
  });
  return { effectiveData, deleteData };
}
/**
 * 生成可编辑表展示数据
 * @param dataSource 可编辑表全部数据
 * @param mergeRowKey 合并列字段
 * @returns 
 */
export function filterDataSource(
  dataSource?: any[],
  mergeRowKey?: string,
): any[] {
  if (!dataSource) return [];
  const effectiveDataSource = dataSource.filter(
    v => v[STATUS_CREATED_CODE] !== STATUS_CREATED.DELETE,
  );
  if (effectiveDataSource.length && mergeRowKey) {
    const sortObj: any = {};
    const sortDataSource: any[] = [];
    effectiveDataSource.forEach(v => {
      const mergeValue: string = v[mergeRowKey];
      if (!sortObj[mergeValue]) {
        sortObj[mergeValue] = [v];
      } else {
        sortObj[mergeValue].push(v);
      }
    });
    for (let [key, value] of Object.entries(sortObj)) {
      if (Array.isArray(value))
        sortDataSource.push({
          [mergeRowKey]: key,
          key: key,
          isMainTable: true,
          children: value,
        });
    }
    return sortDataSource;
  }
  return effectiveDataSource;
}
/**
 * 获取移动后的可编辑表数据
 * @param dataSource 移动之前可编辑表数据
 * @param startIndex 
 * @param endIndex 
 * @returns 
 */
export function getMoveDataSource(dataSource: any[], startIndex: number, endIndex: number): any[] {
  
  let startLineIndex = startIndex;
  let endLineIndex = endIndex;
  if (startIndex > endIndex) {
    startLineIndex = endIndex;
    endLineIndex = startIndex;
  }
  // 移动后的数据
  const moveDataSource: any[] = [];
  // 移动的行数据
  let moveItem: any = {};
  dataSource.forEach((item, index) => {
    if (index >= startLineIndex && index <= endLineIndex) {
      if (index === startIndex) {
        // 移动的行 并移除该行数据 最后重新插入
        moveItem = {
          ...item,
          [STATUS_CREATED_CODE]: updateRecordStatus(item[STATUS_CREATED_CODE]),
        };
      } else {
        // 移动区间内的数据
        moveDataSource.push({
          ...item,
          [STATUS_CREATED_CODE]: updateRecordStatus(item[STATUS_CREATED_CODE]),
        });
      }
    } else {
      // 移动区间外的数据
      moveDataSource.push(item)
    }
  });
  // 插入移动的行数据
  moveDataSource.splice(endIndex, 0, moveItem);
  return moveDataSource;
}
/**
 * 表单传入名称转换成当前行的表单名称
 * @param name 表单传入名称
 * @param rowKey 可编辑表第几行
 * @returns 
 */
export function getFormItemName(rowKey: number, name?: NamePath): NamePath | NamePath[] | undefined {
  if (Array.isArray(name)) {
    return name.map(v => ([GROUP_NAME, rowKey, v]));
  }
  if (name !== undefined) {
    return [GROUP_NAME, rowKey, name];
  }
}
/**
 * 当前行的表单名称转换成SelectTable的表单名称
 * @param name 当前行的表单名称
 * @returns 
 */
export function getSelectTableNames(name?: NamePath): NamePath | NamePath[] | undefined {
  if (Array.isArray(name)) {
    if (name.length === 2) return name;
    if (name.length === 3) return [name];
  }
}
/**
 * 设置上传头像（单图）组件value
 * @param initialValue 默认值
 * @param relativePath 值
 * @returns 
 */
export function dataToUpload(initialValue: any, relativePath: any) {
  if (!initialValue) return initialValue;
  if (typeof initialValue === 'string') {
    return [
      {
        uid: '-1',
        status: 'done',
        url: initialValue,
        thumbUrl: getThumbUrl(initialValue),
        relativePath: relativePath,
      },
    ];
  }
  return initialValue;
}
/**
 * 设置上传图片（多图）组件value
 * @param initialValue 默认值
 * @param relativePath 值
 * @returns 
 */
export function dataToUploads(initialValue: any, relativePath: any) {
  if (!initialValue) return initialValue;
  const result = [];
  if (typeof initialValue === 'string') {
    result.push({
      uid: '-1',
      status: 'done',
      url: initialValue,
      thumbUrl: getThumbUrl(initialValue),
      relativePath: relativePath,
    });
  }
  if (Array.isArray(initialValue)) {
    initialValue.forEach((v, i) => {
      result.push({
        uid: i,
        status: 'done',
        url: v,
        thumbUrl: getThumbUrl(v),
        relativePath: relativePath[i],
      });
    });
  }
  return result;
}
/**
 * 生成统计数据
 * @param totalKeys 统计字段配置
 * @param dataSource 可编辑表数据
 * @returns 
 */
export function getTotalInfo(
  totalKeys?: ITotalItem[],
  dataSource?: any[],
): any {
  let totalData: any = {};
  if (Array.isArray(totalKeys) && Array.isArray(dataSource)) {
    totalKeys.forEach(item => {
      const { dataIndex, format } = item;
      const currentValue = dataIndexTotal(dataIndex, dataSource);
      totalData[dataIndex] = format ? format(currentValue) : currentValue;
    });
  }
  return totalData;
}
/**
 * 根据字段名求和
 * @param dataIndex 字段名
 * @param dataSource 可编辑表数据
 * @returns 
 */
export function dataIndexTotal(dataIndex: string, dataSource: any[]): number {
  let total: number = 0;
  dataSource.forEach(item => {
    if (item[dataIndex]) total = accAdd(total, Number(item[dataIndex]) || 0);
  });
  return total;
}
